<template>
  <div class="safedig-events-page">
    <div class="safedig-events-layout" :class="{ 'safedig-events-calendar-only': viewMode === 'calendar' }">
      <div class="safedig-events-left">
        <div class="safedig-events-view-toggles">
          <button
            type="button"
            class="safedig-events-view-btn"
            :class="{ active: viewMode === 'list' }"
            aria-label="List view"
            @click="viewMode = 'list'"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="safedig-events-icon">
              <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>
          </button>
          <button
            type="button"
            class="safedig-events-view-btn"
            :class="{ active: viewMode === 'calendar' }"
            aria-label="Calendar view"
            @click="viewMode = 'calendar'"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="safedig-events-icon">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
            </svg>
          </button>
        </div>

        <!-- List view -->
        <template v-if="viewMode === 'list'">
          <h1 class="safedig-events-title">{{ $t('events.pageTitle') }}</h1>
          <p class="safedig-events-message">{{ $t('events.noEventsMessage') }}</p>
        </template>

        <!-- Calendar view -->
        <div v-else class="safedig-calendar-wrap">
          <div class="safedig-calendar-header">
            <span class="safedig-calendar-month-year">{{ calendarMonthLabel }}</span>
            <div class="safedig-calendar-nav">
              <button type="button" class="safedig-calendar-nav-btn" aria-label="Previous month" @click="prevMonth">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="safedig-calendar-nav-icon"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" /></svg>
              </button>
              <button type="button" class="safedig-calendar-nav-btn" aria-label="Next month" @click="nextMonth">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="safedig-calendar-nav-icon"><path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" /></svg>
              </button>
            </div>
          </div>
          <div class="safedig-calendar-grid">
            <div v-for="(day, i) in daysShort" :key="'head-' + i" class="safedig-calendar-cell safedig-calendar-cell-head">
              {{ day }}
            </div>
            <div
              v-for="(cell, idx) in calendarCells"
              :key="idx"
              class="safedig-calendar-cell safedig-calendar-cell-day"
              :class="{
                'safedig-calendar-other-month': !cell.isCurrentMonth,
                'safedig-calendar-today': cell.isToday
              }"
            >
              {{ cell.day }}
            </div>
          </div>
        </div>
      </div>
      <div v-show="viewMode === 'list'" class="safedig-events-map-placeholder">
        <!-- Map or featured image placeholder -->
      </div>
    </div>
  </div>
</template>

<style scoped>
.safedig-events-page {
  width: 100%;
  padding: 32px 0 48px;
  background: #fff;
}

.safedig-events-layout {
  display: flex;
  gap: 32px;
  align-items: stretch;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Align calendar right edge with header (header uses 24px; wrapper adds 16px, so use 8px here) */
.safedig-events-layout.safedig-events-calendar-only {
  padding-right: 8px;
}

.safedig-events-left {
  flex: 0 1 auto;
  min-width: 0;
  width: 100%;
  max-width: 420px;
}

.safedig-events-layout.safedig-events-calendar-only .safedig-events-left {
  max-width: none;
  flex: 1 1 auto;
}

.safedig-events-view-toggles {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.safedig-events-view-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  border-radius: 4px;
  transition: color 0.15s, background 0.15s;
}

.safedig-events-view-btn:hover {
  color: #374151;
  background: #f3f4f6;
}

.safedig-events-view-btn.active {
  color: #1e3a5f;
  background: #f3f4f6;
}

.safedig-events-icon {
  width: 20px;
  height: 20px;
}

.safedig-events-title {
  font-size: 28px;
  font-weight: 700;
  color: #374151;
  margin: 0 0 16px;
  line-height: 1.2;
}

.safedig-events-message {
  font-size: 16px;
  color: #6b7280;
  line-height: 1.6;
  margin: 0;
}

.safedig-events-map-placeholder {
  flex: 1;
  min-height: 400px;
  background: #e5e7eb;
  border-radius: 4px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
}

/* Calendar view */
.safedig-calendar-wrap {
  width: 100%;
  max-width: 420px;
}

.safedig-events-calendar-only .safedig-calendar-wrap {
  max-width: 1200px;
}

.safedig-calendar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 16px;
}

.safedig-calendar-month-year {
  font-size: 24px;
  font-weight: 700;
  color: #374151;
}

.safedig-calendar-nav {
  display: flex;
  gap: 4px;
}

.safedig-calendar-nav-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: #3e7799;
  color: #fff;
  cursor: pointer;
  transition: background 0.15s;
}

.safedig-calendar-nav-btn:hover {
  background: #3e7799;
}

.safedig-calendar-nav-icon {
  width: 18px;
  height: 18px;
}

.safedig-calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  border: 1px solid #d1d5db;
  border-radius: 4px;
  overflow: hidden;
  background: #fff;
}

.safedig-calendar-cell {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 6px 8px;
  font-size: 14px;
  border-right: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
}

.safedig-calendar-cell:nth-child(7n) {
  border-right: none;
}

.safedig-calendar-cell-head {
  min-height: 44px;
  background: #3e7799;
  color: #fff;
  font-weight: 600;
  justify-content: center;
  align-items: center;
}

.safedig-calendar-cell-day {
  min-height: 72px;
  color: #0e7490;
}

.safedig-calendar-cell-day.safedig-calendar-other-month {
  color: #9ca3af;
}

.safedig-calendar-cell-day.safedig-calendar-today {
  background: #fef9c3;
  color: #1e3a5f;
}

@media (max-width: 768px) {
  .safedig-events-layout {
    flex-direction: column;
    padding: 0 16px;
  }

  .safedig-events-left {
    max-width: none;
  }

  .safedig-events-map-placeholder {
    min-height: 280px;
  }
}
</style>

<script>
export default {
  name: 'SafeDigEvents',
  data() {
    const now = new Date();
    return {
      viewMode: 'list',
      displayYear: now.getFullYear(),
      displayMonth: now.getMonth()
    };
  },
  computed: {
    daysShort() {
      const list = this.$t('events.daysShort');
      return Array.isArray(list) ? list : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    },
    monthNames() {
      const list = this.$t('events.months');
      return Array.isArray(list) ? list : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    },
    calendarMonthLabel() {
      return `${this.monthNames[this.displayMonth]} ${this.displayYear}`;
    },
    calendarCells() {
      const year = this.displayYear;
      const month = this.displayMonth;
      const first = new Date(year, month, 1);
      const startDow = first.getDay();
      const startDate = new Date(year, month, 1 - startDow);
      const cells = [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      for (let i = 0; i < 42; i++) {
        const d = new Date(startDate);
        d.setDate(startDate.getDate() + i);
        d.setHours(0, 0, 0, 0);
        const isCurrentMonth = d.getMonth() === month;
        const isToday = d.getTime() === today.getTime();
        cells.push({ day: d.getDate(), isCurrentMonth, isToday });
      }
      return cells;
    }
  },
  methods: {
    prevMonth() {
      if (this.displayMonth === 0) {
        this.displayMonth = 11;
        this.displayYear -= 1;
      } else {
        this.displayMonth -= 1;
      }
    },
    nextMonth() {
      if (this.displayMonth === 11) {
        this.displayMonth = 0;
        this.displayYear += 1;
      } else {
        this.displayMonth += 1;
      }
    }
  }
};
</script>
